import { useEffect, useMemo, useState } from "react"
import { useFormik } from "formik"
import withRouter from "Components/Common/withRouter"
import FormPageTemplate from "Components/templates/form-page-template"
import DepartmentForm from "Components/organisms/forms/department-form"
import DepartmentFields from "Components/organisms/forms/department-form/types"
import { DepartmentFormValidation } from "data/department-form"
import { DepartmentLevel } from "models/department-level"
import Department from "models/department"
import Employee from "models/employee"
import { getDepartmentFormFields, submitCreateDepartmentForm } from "slices/admin/departments/thunk"
import { navigateToDepartments, navigateToDepartmentDetails } from "utils/navigator"

const CreateDepartmentPage = (props: any) => {
    document.title = "Create Department | IbexIQ";

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [departmentLevels, setDepartmentLevels] = useState<DepartmentLevel[]>([])
    const [departments, setDepartments] = useState<Department[]>([])
    const [employees, setEmployees] = useState<Employee[]>([])

    useEffect(() => {
        const init = async () => {
            const res = await getDepartmentFormFields()
            if (res) {
                setDepartmentLevels(res.departmentLevels)
                setDepartments(res.departments)
                setEmployees(res.employees)
            }
            setIsLoading(false)
        }
        init()
    }, [])

    const handleBack = () => navigateToDepartments(props.router.navigate)

    const onSubmit = async (vals: DepartmentFields) => {
        const res = await submitCreateDepartmentForm(vals)
        if (res) {
            navigateToDepartmentDetails(props.router.navigate, res.id)
        }
    }

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        isValid,
        isSubmitting,
        setFieldValue,
    } = useFormik({
        initialValues: {} as DepartmentFields,
        onSubmit,
        validationSchema: DepartmentFormValidation
    })

    // A new department starts with its parent's billable flag; the admin can still change it.
    useEffect(() => {
        const parent = departments.find(d => d.id === values.parentDepartmentId)
        setFieldValue('isBillable', parent?.isBillable ?? true)
    }, [values.parentDepartmentId, departments])

    const departmentLevelOptions = useMemo(() => departmentLevels.map(l => ({
        value: l.id, label: l.description
    })), [departmentLevels])

    const parentDepartmentOptions = useMemo(() => departments.map(d => ({
        value: d.id, label: `${d.code} — ${d.description}`
    })), [departments])

    const employeeOptions = useMemo(() => employees.map(e => ({
        value: e.id, label: e.commonName ?? `${e.firstName} ${e.lastName}`
    })), [employees])

    return <FormPageTemplate
        title="Admin"
        breadcrumbItem="Create Department"
        isLoading={isLoading}
        form={<DepartmentForm
            departmentLevels={departmentLevelOptions}
            parentDepartments={parentDepartmentOptions}
            employees={employeeOptions}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            handleCancel={handleBack}
            isValid={isValid}
            isSubmitting={isSubmitting}
            setFieldValue={setFieldValue}
        />}
    />
}

export default withRouter(CreateDepartmentPage)
